// Placeholder shown at the tester /projects routes while cloud/headless
// dashboard testing is paused. The sidebar tab is disabled, but this hard-blocks
// anyone who deep-links the URL directly. Re-enabling is just a matter of
// restoring the old routes in App.jsx and dropping the `disabled` flag in nav.js.
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FolderKanban, PauseCircle, Sparkles, Bot, Puzzle, ArrowRight } from "lucide-react";
import Aurora from "@/components/common/Aurora";
import Button from "@/components/ui/Button";

const ALTERNATIVES = [
  {
    to: "/dashboard/generate",
    icon: Sparkles,
    title: "Generate Test Cases",
    desc: "Create a project, add logins, and generate AI test cases.",
  },
  {
    to: "/dashboard/extension",
    icon: Puzzle,
    title: "Extension",
    desc: "Drive tests live in your own Chrome tab.",
  },
  {
    to: "/dashboard/exe",
    icon: Bot,
    title: "Exe Agent",
    desc: "Run suites on your machine with the desktop agent.",
  },
];

export default function ProjectsDisabled() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="relative mx-auto max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-soft dark:border-white/10 dark:bg-ink-900 sm:p-10"
    >
      <Aurora className="opacity-50" />
      <div className="relative flex flex-col items-center gap-4">
        <span className="grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-slate-400 to-slate-600 text-white shadow-glow">
          <FolderKanban size={26} />
        </span>

        <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-600 dark:bg-rose-500/10 dark:text-rose-400">
          <PauseCircle size={14} /> Temporarily disabled
        </span>

        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
          Projects is temporarily unavailable
        </h1>
        <p className="max-w-md text-sm text-slate-500 dark:text-slate-400">
          Cloud (headless) dashboard test runs are paused while we improve them.
          You can still generate test cases and run tests live from the options below.
        </p>

        <div className="mt-4 grid w-full gap-3 sm:grid-cols-3">
          {ALTERNATIVES.map((a) => (
            <Link
              key={a.to}
              to={a.to}
              className="group flex flex-col items-start gap-2 rounded-xl border border-slate-200 bg-slate-50/70 p-4 text-left transition-colors hover:border-brand-300 hover:bg-brand-50/50 dark:border-white/10 dark:bg-white/5 dark:hover:border-brand-500/40 dark:hover:bg-brand-500/10"
            >
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-brand-500/10 text-brand-600 transition-transform group-hover:scale-105 dark:text-brand-300">
                <a.icon size={18} />
              </span>
              <span className="text-sm font-semibold text-slate-800 dark:text-white">{a.title}</span>
              <span className="text-xs text-slate-500 dark:text-slate-400">{a.desc}</span>
            </Link>
          ))}
        </div>

        <Button to="/dashboard/generate" size="sm" className="mt-2">
          Go to Generate Test Cases <ArrowRight size={15} />
        </Button>
      </div>
    </motion.div>
  );
}
